import {
  ContractorType,
  CustomerUpsert,
  OrderDetail
} from '@insurance/api/requests'
import { AppPermissions } from '@shared/types'
import {
  extractSeriesAndNumber,
  formatDateToISOFormat,
  replaceEmptyWithUndefined
} from '@shared/utils'

import { Contractor, Person } from './types'

type InsuranceValue = {
  contractor?: Contractor
  same_contractor_as_first_person?: boolean
  persons?: Person[]

  beneficiary?: Contractor
  insured_person?: Contractor
  insured_same_as_contractor?: boolean
  set_beneficiary_data?: boolean
}

type OptionsGetContractor = {
  forceResident?: boolean
}

export const getContractorFields = (
  values?: InsuranceValue,
  options?: OptionsGetContractor
) => {
  const isLegalEntity =
    values?.contractor?.content_type === ContractorType.LEGALENTITY

  const person = values?.same_contractor_as_first_person
    ? values?.persons?.[0]
    : values?.contractor?.person

  const resident = options?.forceResident || person?.resident

  Object.keys(person || {}).forEach((k) => {
    const key = k as keyof Person

    if (person?.[key] === null || person?.[key] === undefined)
      delete person?.[key]
  })

  return {
    content_type: values?.contractor?.content_type,

    ...(isLegalEntity && {
      legal_entity: {
        ...replaceEmptyWithUndefined(values?.contractor?.legal_entity),
        phone: undefined
      }
    }),

    ...(!isLegalEntity && {
      person: {
        ...replaceEmptyWithUndefined({
          ...values?.contractor?.person,
          ...person
        }),
        date_of_birth: formatDateToISOFormat(person?.date_of_birth),
        document: extractSeriesAndNumber(person?.document || ''),
        phone: undefined,

        ...(resident
          ? { country: undefined }
          : {
              district: undefined,
              location: undefined,
              street: undefined,
              number: undefined,
              block: undefined,
              apartment: undefined
            })
      }
    })
  }
}

export const getBeneficiaryFields = (values?: InsuranceValue) => {
  const isLegalEntity =
    values?.beneficiary?.content_type === ContractorType.LEGALENTITY

  if (!values?.set_beneficiary_data || !values?.beneficiary?.content_type)
    return undefined

  return {
    content_type: values?.beneficiary?.content_type,

    ...(isLegalEntity && {
      legal_entity: {
        ...replaceEmptyWithUndefined(values?.beneficiary?.legal_entity),
        phone: undefined
      }
    }),

    ...(!isLegalEntity && {
      person: {
        ...replaceEmptyWithUndefined(values?.beneficiary?.person),
        date_of_birth: formatDateToISOFormat(
          values?.beneficiary?.person?.date_of_birth
        ),
        phone: undefined
      }
    })
  }
}

export const getInsuredPersonFields = (values?: InsuranceValue) => {
  const isLegalEntity =
    values?.insured_person?.content_type === ContractorType.LEGALENTITY

  if (
    values?.insured_same_as_contractor ||
    !values?.insured_person?.content_type
  )
    return undefined

  return {
    content_type: values?.insured_person?.content_type,

    ...(isLegalEntity && {
      legal_entity: {
        ...replaceEmptyWithUndefined(values?.insured_person?.legal_entity),
        phone: undefined
      }
    }),

    ...(!isLegalEntity && {
      person: {
        ...replaceEmptyWithUndefined(values?.insured_person?.person),
        date_of_birth: formatDateToISOFormat(
          values?.insured_person?.person?.date_of_birth
        ),
        phone: undefined
      }
    })
  }
}

const initValues = {
  content_type: ContractorType.PERSON,
  person: { resident: true },
  legal_entity: undefined
}

export const iniMixtContractorValues = {
  contractor: initValues,
  beneficiary: initValues,
  insured_person: initValues,
  insured_same_as_contractor: true,
  set_beneficiary_data: false
}

export const getMixtContractorFields = (values?: InsuranceValue) => {
  return {
    contractor: getContractorFields(values),
    beneficiary: getBeneficiaryFields(values),
    insured_person: getInsuredPersonFields(values),

    insured_same_as_contractor: values?.insured_same_as_contractor
  }
}

export const getCustomerFields = (values?: CustomerUpsert) => {
  return replaceEmptyWithUndefined({
    ...values,
    date_of_birth: undefined
  }) as CustomerUpsert
}

type SerializeContractorOptions = {
  hasPermission: (permissions?: AppPermissions[]) => boolean
}

export const serializeContractorFields = (
  { hasPermission }: SerializeContractorOptions,
  data: OrderDetail['data']
) => {
  const hasFiscalCodePermission = hasPermission([
    AppPermissions.FISCAL_CODE_ENTITY
  ])

  if (hasFiscalCodePermission) return

  const contractorFiscalCode = data?.contractor?.legal_entity?.is_fiscal_code
  const beneficiaryFiscalCode = data?.beneficiary?.legal_entity?.is_fiscal_code
  const insuredPerson = data?.insured_person?.legal_entity?.is_fiscal_code

  if (contractorFiscalCode)
    data.contractor.legal_entity.is_fiscal_code = undefined

  if (beneficiaryFiscalCode)
    data.beneficiary.legal_entity.is_fiscal_code = undefined

  if (insuredPerson) data.insured_person.legal_entity.is_fiscal_code = undefined
}
